import isEmpty from "lodash.isempty";
import { formatFullStreet } from "../../util/format";
import {
  mapCountry,
  mapDateDocumentToPageModel,
  mapDatePageModelToRequest,
  mapNationality,
  mapNationalityString,
  mapYesNoStringToBoolean,
} from "../shared/mappers";
import { defaults } from "./defaults";
import { OwnerType, RegulatoryInformationModel } from "./types";
import {
  BasicAccountApplicationDocument,
  BeneficialOwnerDto,
  ControllingOwnerDto,
  NaturalPersonDocument,
  RegulatoryInfoDto,
} from "../../api/commercial/generated-types";
import { Gender } from "../../types";
import {
  isCompanyClassificationBeneficialOwnerCase,
  isCompanyClassificationControllingOwnersCase,
} from "./helpers";
import { LegalForm } from "../home/types";

const mapAdminToPageModel = (
  personDocument: NaturalPersonDocument,
  isSameAsAdmin: boolean,
): OwnerType => {
  return {
    nationality: mapNationality(personDocument.nationality),
    birthdate: mapDateDocumentToPageModel(personDocument.birthdate),
    gender: personDocument.gender,
    firstName: personDocument.firstName,
    lastName: personDocument.lastName,
    fullStreet: formatFullStreet(personDocument.address),
    address: {
      ...personDocument.address,
      country: mapCountry(
        personDocument.address ? personDocument.address.country : null,
      ),
    },
    isSameAsAdmin,
  };
};

const mapControllingOwners = (
  document: BasicAccountApplicationDocument,
): RegulatoryInformationModel["controllingOwners"] => {
  const adminCaseControllingOwners: RegulatoryInformationModel["controllingOwners"]["admin"] =
    [];
  const otherCaseControllingOwners: RegulatoryInformationModel["controllingOwners"]["other"] =
    [];

  if (document.adminIsControllingOwner) {
    adminCaseControllingOwners.push(mapAdminToPageModel(document.admin, true));
    document.controllingOwners.forEach(
      (controllingOwner: NaturalPersonDocument) =>
        adminCaseControllingOwners.push(
          mapAdminToPageModel(controllingOwner, false),
        ),
    );

    otherCaseControllingOwners.push(defaults.controllingOwners.other[0]);
  } else {
    adminCaseControllingOwners.push(mapAdminToPageModel(document.admin, true));

    if (document.controllingOwners.length === 0) {
      otherCaseControllingOwners.push(defaults.controllingOwners.other[0]);
    } else {
      document.controllingOwners.forEach(
        (controllingOwner: NaturalPersonDocument) =>
          otherCaseControllingOwners.push(
            mapAdminToPageModel(controllingOwner, false),
          ),
      );
    }
  }

  return {
    admin: adminCaseControllingOwners,
    other: otherCaseControllingOwners,
  };
};

const mapBeneficialOwner = (
  document: BasicAccountApplicationDocument,
): RegulatoryInformationModel["beneficialOwner"] => {
  const beneficialOwners: OwnerType = isEmpty(document.beneficialOwner)
    ? defaults.beneficialOwner.other
    : mapAdminToPageModel(document.beneficialOwner, false);

  return {
    admin: mapAdminToPageModel(document.admin, true),
    other: beneficialOwners,
  };
};

const mapDocumentToPageModel = (
  document: BasicAccountApplicationDocument,
  companyLegalForm: LegalForm,
): RegulatoryInformationModel => {
  const adminDocument: NaturalPersonDocument = document.admin;

  const isAdminOwner: boolean =
    document.adminIsBeneficialOwner || document.adminIsControllingOwner;

  const admin: OwnerType = mapAdminToPageModel(adminDocument, true);

  const controllingOwners = mapControllingOwners(document);

  const beneficialOwner = mapBeneficialOwner(document);

  return {
    admin: admin,
    isOwnerSameAsAdmin: isAdminOwner ? "yes" : "no",
    isSoleProprietorship: companyLegalForm === LegalForm.SOLE_PROPRIETORSHIP,
    beneficialOwner,
    controllingOwners,
    companyClassification: document.companyClassification,
    controllerIdentification: document.controllerIdentification,
    employsOwnStaff: document.employsOwnStaff,
    hasOwnOffice: document.hasOwnOffice,
  };
};

const mapPageModelToRequest = (
  data: RegulatoryInformationModel,
  basicAccountApplicationRef: string,
): RegulatoryInfoDto => {
  let controllingOwners: OwnerType[] = [];
  let beneficialOwners: OwnerType;

  if (
    isCompanyClassificationControllingOwnersCase(data.companyClassification)
  ) {
    controllingOwners = mapYesNoStringToBoolean(data.isOwnerSameAsAdmin)
      ? data.controllingOwners.admin
      : data.controllingOwners.other;
  } else if (
    isCompanyClassificationBeneficialOwnerCase(
      data.companyClassification,
      data.isSoleProprietorship,
    )
  ) {
    beneficialOwners = mapYesNoStringToBoolean(data.isOwnerSameAsAdmin)
      ? data.beneficialOwner.admin
      : data.beneficialOwner.other;
  }

  return {
    basicAccountApplicationRef,
    controllingOwners: controllingOwners
      ? controllingOwners.map((owner: OwnerType) => mapOwner(owner))
      : null,
    beneficialOwner: beneficialOwners ? mapOwner(beneficialOwners) : null,
    companyClassification: data.companyClassification,
    controllerIdentification: data.controllerIdentification,
    hasOwnOffice: data.hasOwnOffice,
    employsOwnStaff: data.employsOwnStaff,
  };
};

const mapOwner = (o: OwnerType): ControllingOwnerDto | BeneficialOwnerDto => {
  return {
    gender: o.gender as Gender,
    firstName: o.firstName,
    lastName: o.lastName,
    address: { ...o.address, country: mapCountry(o.address.country) },
    birthdate: mapDatePageModelToRequest(o.birthdate),
    nationality: mapNationalityString(o.nationality),
    isSameAsAdmin: o.isSameAsAdmin,
  };
};

const RegulatoryInformationMapper = {
  mapDocumentToPageModel,
  mapPageModelToRequest,
};

export default RegulatoryInformationMapper;
